import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Send, X, Sparkles, Loader2 } from "lucide-react";
import { getItem as ssGet, setItem as ssSet } from "@/lib/safe-storage";
import { pickChips } from "@/components/buddybee-chips";

/**
 * BuddyBee — the SINGLE in-app AI assistant for TeeBee.
 *
 * Behaviour matrix:
 *   • Logged-out: friendly help about TeeBee, public features, pricing.
 *                 Protected actions route to /signup or /login.
 *   • Logged-in:  full power — page-aware contextual chips, deep-link launchers,
 *                 chat memory via session_id.
 *
 * Visual: floating bottom-right launcher, glassy panel, animated bee mascot.
 */
// Hide BuddyBee on full-page marketing surfaces — those pages have their own
// help affordances and BuddyBee would visually clash with the auth form / legal text.
const HIDDEN_PATHS = ["/login", "/signup"];
const HIDDEN_PREFIXES = ["/legal"];

const PAGE_HINTS = {
  // Map mentioned-page-names to actual routes for the auto-nav action.
  // Keys are LOWERCASE substrings checked against the model's "Open: X" mention.
  "coins wallet":     "/wallet",
  "wallet":           "/wallet",
  "recharge":         "/wallet",
  "resume builder":   "/resume",
  "resume":           "/resume",
  "marketplace":      "/marketplace",
  "explore":          "/explore",
  "jobs":             "/jobs",
  "applications":     "/applications",
  "career copilot":   "/career",
  "career":           "/career",
  "public profile":   "/profile",
  "profile editor":   "/profile",
  "profile":          "/profile",
  "settings":         "/settings",
  "library":          "/library",
  "lesson workspace": "/lessons",
  "lessons":          "/lessons",
  "teaching assistant": "/teaching-assistant",
  "teaching":         "/teaching-assistant",
  "ocr":              "/ocr",
  "scanner":          "/ocr",
  "knowledge hub":    "/knowledge-hub",
  "dashboard":        "/dashboard",
  "legal":            "/legal",
  "refer":            "/refer",
};

function detectOpenIntent(content) {
  // Match "Open: <page name>" where the page name is letters/spaces/slash, optionally
  // wrapped in markdown bold (**...**). Closing boundary is any non-alpha character
  // (.,!?(): newline or end) so the regex catches parenthetical and punctuation tails.
  const m = content.match(/Open:\s*\*{0,2}([A-Za-z][A-Za-z\s/]+?)\*{0,2}(?=[^A-Za-z\s/]|$)/i);
  if (!m) return null;
  const raw = m[1].trim();
  const target = raw.toLowerCase();
  for (const key of Object.keys(PAGE_HINTS)) {
    if (target.includes(key)) return { label: raw, path: PAGE_HINTS[key] };
  }
  return null;
}

const STORAGE_KEY = "teebee_buddybee_open_v1";

export default function BuddyBee() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Don't render on public landing-style pages where the page itself is the marketing
  // surface — login & signup also include their own help affordances.
  const hide = HIDDEN_PATHS.includes(pathname) || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  // Lazy initializer reads sessionStorage on first render — avoids
  // the react-hooks/set-state-in-effect lint warning.
  // NOTE: only a "0"/"1" UI-open flag — no PII or auth state ever stored here.
  const [open, setOpen] = useState(() => ssGet(STORAGE_KEY, { persistent: false }) === "1");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState(() => [
    {
      id: "welcome",
      role: "assistant",
      content: user
        ? "Hi! I'm **BuddyBee** 🐝. Buzzing in to help — ask me anything, or pick a quick action below."
        : "Hi! I'm **BuddyBee** 🐝, TeeBee's AI companion. Ask me about TeeBee, coins, marketplace, or how to get started.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Page + auth aware chip set
  const chips = useMemo(
    () => pickChips({ authed: !!user, pathname }),
    [user, pathname],
  );

  // Persist open state across navigations (within the session) via sessionStorage.
  useEffect(() => {
    ssSet(STORAGE_KEY, open ? "1" : "0", { persistent: false });
  }, [open]);

  // Auto-scroll on new message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Focus input when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const ask = async (text) => {
    if (!text?.trim() || busy) return;
    if (!user) {
      // Logged-out → answer locally with a knowledge nudge + sign-in CTA for protected stuff.
      setMessages((m) => [
        ...m,
        { id: `u-${Date.now()}`, role: "user", content: text },
        {
          id: `a-${Date.now() + 1}`,
          role: "assistant",
          content:
            "Great question! For full answers I'll need you to **sign in** — your TeeBee account lets me tailor things to you. Meanwhile, [Explore lessons →](/explore) or [Sign up free →](/signup).",
        },
      ]);
      return;
    }

    setBusy(true);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: text }]);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId,
        message: text,
        kind: "buddybee",
      });
      setSessionId(data.session_id);
      const reply = data.assistant_message?.content || "Sorry, I lost the buzz. Try again?";
      const intent = detectOpenIntent(reply);
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", content: reply, intent },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          id: `e-${Date.now()}`,
          role: "assistant",
          content:
            "Something went wrong on my side 🐝 — please try again in a moment, or reach support at **support@teebee.ai**.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    ask(text);
  };

  if (hide) return null;

  return (
    <>
      {/* ─── Floating Launcher ─── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 group select-none"
          aria-label="Open BuddyBee help"
          data-testid="buddybee-launcher"
        >
          <span className="absolute inset-0 rounded-full bg-amber-300/50 dark:bg-amber-300/30 animate-ping" />
          <span className="relative inline-grid place-items-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500 shadow-[0_15px_40px_-10px_rgba(245,158,11,0.55)] ring-2 ring-white dark:ring-zinc-900 transition-transform group-hover:scale-105 active:scale-95">
            <BeeMascot className="h-9 w-9 sm:h-11 sm:w-11" />
          </span>
          <span className="absolute -top-2 -left-32 hidden sm:flex items-center gap-1.5 bg-zinc-900 text-white text-[11px] font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-lg">
            <Sparkles className="h-3 w-3 text-amber-300" /> Ask BuddyBee
          </span>
        </button>
      )}

      {/* ─── Expanded Chat Panel ─── */}
      {open && (
        <div
          className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 max-h-[80vh] rounded-3xl border border-border bg-card shadow-2xl backdrop-blur-xl flex flex-col overflow-hidden"
          data-testid="buddybee-panel"
          role="dialog"
          aria-label="BuddyBee support chat"
        >
          {/* Header — playful gradient with animated bee */}
          <div className="relative bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 px-4 py-3 text-white overflow-hidden">
            <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden>
              <BeeFlight />
            </div>
            <div className="relative flex items-center gap-3">
              <span className="h-10 w-10 grid place-items-center rounded-full bg-white/20 ring-2 ring-white/30 shrink-0">
                <BeeMascot className="h-7 w-7" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold leading-tight text-sm">BuddyBee 🐝</p>
                <p className="text-[11px] text-white/85 leading-tight">Your TeeBee support companion</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/20 transition-colors shrink-0"
                aria-label="Close BuddyBee"
                data-testid="buddybee-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Conversation */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gradient-to-b from-amber-50/30 via-background to-background dark:from-amber-500/5"
            data-testid="buddybee-messages"
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} tb-fade-up`}
              >
                <div className="max-w-[85%] space-y-2">
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-violet-600 text-white rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    }`}
                  >
                    {m.content.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^\)]+\))/g).map((chunk, i) => {
                      const linkMatch = chunk.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
                      if (linkMatch) {
                        return (
                          <button
                            key={`l-${i}`}
                            onClick={() => navigate(linkMatch[2])}
                            className="underline underline-offset-2 font-medium hover:opacity-80"
                          >{linkMatch[1]}</button>
                        );
                      }
                      const boldMatch = chunk.match(/^\*\*([^*]+)\*\*$/);
                      if (boldMatch) return <strong key={`b-${i}`}>{boldMatch[1]}</strong>;
                      return <span key={`t-${i}`}>{chunk}</span>;
                    })}
                  </div>
                  {m.intent && (
                    <button
                      onClick={() => navigate(m.intent.path)}
                      className="text-[11px] tb-chip-lavender rounded-full px-3 py-1 font-medium hover:opacity-90 transition-opacity"
                      data-testid="buddybee-nav-cta"
                    >
                      → Open {m.intent.label}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start tb-fade-up">
                <div className="px-3.5 py-2.5 rounded-2xl bg-muted rounded-bl-md inline-flex items-center gap-2 text-[12px] text-muted-foreground">
                  <BeeMascot className="h-5 w-5 animate-bounce" />
                  <span>BuddyBee is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick chips — page + auth aware. Always shown so users have a clear path. */}
          {!busy && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="buddybee-chips">
              {chips.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    if (c.launch) {
                      // Power-launcher — navigate + close panel
                      const target = c.launch;
                      setOpen(false);
                      if (target.startsWith("/#")) {
                        // anchor link on landing — let the browser handle it
                        window.location.assign(target);
                      } else {
                        navigate(target);
                      }
                    } else {
                      setInput("");
                      ask(c.text);
                    }
                  }}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-border bg-card hover:border-amber-400/60 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors text-foreground/80"
                  data-testid={`buddybee-chip-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form onSubmit={onSubmit} className="px-3 py-3 border-t border-border bg-card">
            <div className="flex items-center gap-2 rounded-full border border-border bg-background pl-3.5 pr-1 py-1">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask BuddyBee anything…"
                disabled={busy}
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground py-1.5"
                data-testid="buddybee-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="h-8 w-8 grid place-items-center rounded-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="buddybee-send"
                aria-label="Send"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground text-center">
              BuddyBee helps you use TeeBee better.
            </p>
          </form>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 *  Premium bee mascot — pure SVG (no asset request, scales crisply, light+dark)
 *  Composed of: yellow body with stripes, transparent wings, tiny smile.
 * ──────────────────────────────────────────────────────────────────────────── */
function BeeMascot({ className = "h-8 w-8" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
      {/* wings (back, top) */}
      <ellipse cx="20" cy="20" rx="11" ry="7" fill="#fff" opacity="0.85" />
      <ellipse cx="44" cy="20" rx="11" ry="7" fill="#fff" opacity="0.85" />
      <ellipse cx="20" cy="20" rx="11" ry="7" fill="none" stroke="#f59e0b" strokeWidth="1" opacity="0.4" />
      <ellipse cx="44" cy="20" rx="11" ry="7" fill="none" stroke="#f59e0b" strokeWidth="1" opacity="0.4" />
      {/* body */}
      <ellipse cx="32" cy="36" rx="18" ry="16" fill="#fcd34d" />
      {/* stripes */}
      <path d="M22 28 Q32 24 42 28 L40 32 Q32 28 24 32 Z" fill="#1f2937" opacity="0.85" />
      <path d="M20 38 Q32 34 44 38 L42 42 Q32 38 22 42 Z" fill="#1f2937" opacity="0.85" />
      <path d="M22 48 Q32 50 42 48 L40 50 Q32 52 24 50 Z" fill="#1f2937" opacity="0.85" />
      {/* eyes */}
      <circle cx="26" cy="32" r="2.2" fill="#1f2937" />
      <circle cx="38" cy="32" r="2.2" fill="#1f2937" />
      <circle cx="26.6" cy="31.4" r="0.7" fill="#fff" />
      <circle cx="38.6" cy="31.4" r="0.7" fill="#fff" />
      {/* smile */}
      <path d="M28 38 Q32 41 36 38" stroke="#1f2937" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* antennae */}
      <path d="M27 22 Q24 17 22 14" stroke="#1f2937" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <path d="M37 22 Q40 17 42 14" stroke="#1f2937" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <circle cx="22" cy="14" r="1.5" fill="#1f2937" />
      <circle cx="42" cy="14" r="1.5" fill="#1f2937" />
    </svg>
  );
}

/* Playful flight path animation across the chat header */
function BeeFlight() {
  return (
    <svg viewBox="0 0 200 60" className="w-full h-full" preserveAspectRatio="none" aria-hidden>
      <defs>
        <path id="bee-path" d="M -10 30 Q 50 5, 100 30 T 210 30" />
      </defs>
      <g style={{ opacity: 0.9 }}>
        <BeeMascot className="h-4 w-4" />
        <use href="#bee-path" stroke="#fff" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="2 4" fill="none" />
        <g>
          <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
            <mpath href="#bee-path" />
          </animateMotion>
          <g transform="translate(-4 -4)">
            <BeeMascot className="h-3 w-3" />
          </g>
        </g>
      </g>
    </svg>
  );
}
